import { useState } from "react";
import type { AuthUser } from "../types/index.js";

interface UserAvatarProps {
	user: AuthUser;
	size?: number;
}

/**
 * Derives up to two initials from a display name.
 * "Vivek Garg"  → "VG"
 * "Vivek"       → "V"
 * "Mary Jane Watson" → "MW"
 */
function getInitials(displayName: string): string {
	const words = displayName.trim().split(/\s+/).filter(Boolean);
	if (words.length === 0) return "?";
	if (words.length === 1) return words[0].charAt(0).toUpperCase();
	return (words[0].charAt(0) + words[words.length - 1].charAt(0)).toUpperCase();
}

/**
 * Circular avatar component.
 * Renders a profile picture when available, falling back to coloured initials.
 */
export default function UserAvatar({ user, size = 36 }: UserAvatarProps) {
	const [imgError, setImgError] = useState(false);

	const style: React.CSSProperties = {
		width: size,
		height: size,
		fontSize: Math.round(size * 0.38),
	};

	if (user.picture && !imgError) {
		return (
			<img
				className="user-avatar"
				src={user.picture}
				alt={user.displayName}
				width={size}
				height={size}
				style={style}
				onError={() => setImgError(true)}
			/>
		);
	}

	return (
		<span className="user-avatar user-avatar--initials" style={style} aria-hidden="true">
			{getInitials(user.displayName)}
		</span>
	);
}
